'use client';

import React, { useState, useRef, useEffect } from 'react';
import { IoMdCloseCircle } from "react-icons/io";
import { FaCircleNotch } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { useRouter } from "next/navigation";

export default function DeleteReview({ reviewid }: { reviewid: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_URL}productreviewsid/${reviewid}`, {
        method: "DELETE",
        headers: {
          "Authorization": "Token " + process.env.NEXT_PUBLIC_TOKEN,
        },
      });

      if (!response.ok) {
        const rawResponse = await response.text();
        const errorData = JSON.parse(rawResponse);
        setError(errorData.message || 'Delete failed');
        return;
      }

      setIsOpen(false);
      router.refresh();
    } catch (error) {
      console.error('Error during deletion', error);
      setError('Network error');
    } finally {
      window.location.reload();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <MdDeleteOutline size={18} className="cursor-pointer text-red-500" onClick={() => setIsOpen(true)} />

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            ref={dialogRef}
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative"
          >
            <IoMdCloseCircle
              size={24}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={() => setIsOpen(false)}
            />

            <h2 className="text-xl font-semibold text-gray-800 mb-4 uppercase">Delete Review</h2>
            <p className="text-gray-600 mb-6 text-sm">Are you sure you want to delete this review? This action cannot be undone.</p>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors uppercase font-medium"
              >
                Cancel
              </button>

              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="w-full py-1 rounded-lg bg-yellow-500 text-white hover:bg-black transition-colors uppercase font-medium flex gap-2 justify-center items-center"
              >
                Delete
                {isLoading && <FaCircleNotch className="animate-spin w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}